/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ClipboardText, Flask, ArrowUpRight, CheckCircle, Warning, FloppyDisk } from '@phosphor-icons/react';
import TI from '../../components/brand/TI';
import { HaleWordmark } from '../../components/HaleBrand';
import SummaryCards from '../../components/hale-review/SummaryCards';
import AuditTimeline, { agStageIndex } from '../../components/hale-review/AuditTimeline';
import DocumentChecklist, { mergeChecklist } from '../../components/hale-review/DocumentChecklist';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IVORY = '#F5F1E8';
const SLATE = '#0B1426';
const CORAL = '#FF4E6A';
const MUTED = '#5B6577';
const RULE  = 'rgba(11,20,38,0.10)';

/* /assessor/hale-review — AG / ToxIntel reviewer console.
 *
 * Implements the Agyekum Group / ToxIntel scientific authority surface:
 *   - Pull intake queue
 *   - Open a submission to record scientific audit (internal_notes,
 *     released_findings) and pick a recommendation
 *   - Commit the recommendation to hand off to Hale program decisions
 *
 * Strictly scoped to scientific output — does NOT issue badges or make
 * final Hale program decisions (those live in /admin/hale/decisions).
 */

function StatusPill({ value, options, accent = CORAL }) {
  if (!value) return <span className="text-[11px]" style={{ color: MUTED }}>—</span>;
  const opt = (options || []).find(o => o.id === value);
  return (
    <span
      className="inline-flex items-center px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-[0.18em]"
      style={{ backgroundColor: 'rgba(11,20,38,0.06)', color: accent }}
    >
      {opt?.label || value}
    </span>
  );
}

function ReviewerEditor({ app, options, onSaved }) {
  const [internalNotes, setInternalNotes] = useState(app.ag?.internal_notes || '');
  const [releasedFindings, setReleasedFindings] = useState(app.ag?.released_findings || '');
  const [recommendation, setRecommendation] = useState(app.ag?.recommendation || '');
  const [conditions, setConditions] = useState((app.ag?.recommendation_conditions || []).join('\n'));
  const [checklist, setChecklist] = useState(mergeChecklist(app.ag?.document_checklist));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset local state when a different application is opened.
  useEffect(() => {
    setInternalNotes(app.ag?.internal_notes || '');
    setReleasedFindings(app.ag?.released_findings || '');
    setRecommendation(app.ag?.recommendation || '');
    setConditions((app.ag?.recommendation_conditions || []).join('\n'));
    setChecklist(mergeChecklist(app.ag?.document_checklist));
    setError(null); setSuccess(null);
  }, [app.id, app.ag?.internal_notes, app.ag?.released_findings, app.ag?.recommendation,
      app.ag?.recommendation_conditions, app.ag?.document_checklist]);

  const saveDraft = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const r = await axios.patch(
        `${API_URL}/api/hale-review/application/${app.id}/ag`,
        {
          internal_notes: internalNotes,
          released_findings: releasedFindings,
          recommendation: recommendation || null,
          recommendation_conditions: conditions.split('\n').map(s => s.trim()).filter(Boolean),
          document_checklist: checklist,
        },
        { withCredentials: true },
      );
      onSaved(r.data);
      setSuccess('Draft saved.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const commit = async () => {
    if (!recommendation) { setError('Pick a recommendation before completing the review.'); return; }
    setSaving(true); setError(null); setSuccess(null);
    try {
      // Persist the checklist alongside the commit so the AG record is
      // fully captured before hand-off (the commit endpoint itself only
      // sets the recommendation block — checklist lives under PATCH /ag).
      await axios.patch(
        `${API_URL}/api/hale-review/application/${app.id}/ag`,
        { document_checklist: checklist },
        { withCredentials: true },
      ).catch(() => {});
      const r = await axios.post(
        `${API_URL}/api/hale-review/application/${app.id}/ag/complete`,
        {
          recommendation,
          recommendation_conditions: conditions.split('\n').map(s => s.trim()).filter(Boolean),
          released_findings: releasedFindings,
          internal_notes: internalNotes,
        },
        { withCredentials: true },
      );
      onSaved(r.data);
      setSuccess('Scientific output committed and routed to Hale.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Commit failed.');
    } finally { setSaving(false); }
  };

  const handedOff = app.hale?.hale_status === 'ag_recommendation_received'
    || app.hale?.hale_status === 'hale_approved'
    || app.hale?.hale_status === 'hale_rejected'
    || app.hale?.hale_status === 'badge_issued';

  return (
    <div className="space-y-5" data-testid={`reviewer-editor-${app.id}`}>
      <DocumentChecklist value={checklist} onChange={setChecklist} disabled={false} />

      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
          Internal reviewer notes · never released to brand
        </p>
        <textarea
          rows={5}
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          className="w-full text-sm p-3 rounded-sm border bg-white"
          style={{ borderColor: RULE, color: SLATE }}
          data-testid="reviewer-internal-notes"
        />
      </div>
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: CORAL }}>
          Released findings · shared with Hale (and brand after Hale decision)
        </p>
        <textarea
          rows={6}
          value={releasedFindings}
          onChange={(e) => setReleasedFindings(e.target.value)}
          className="w-full text-sm p-3 rounded-sm border bg-white"
          style={{ borderColor: RULE, color: SLATE }}
          data-testid="reviewer-released-findings"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
            Recommendation
          </p>
          <select
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
            className="w-full text-sm p-2.5 rounded-sm border bg-white"
            style={{ borderColor: RULE, color: SLATE }}
            data-testid="reviewer-recommendation"
          >
            <option value="">Pick a recommendation…</option>
            {(options?.ag_recommendations || []).map(o => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
          <p className="text-[11px] mt-2 leading-snug" style={{ color: MUTED }} data-testid="reviewer-helper-text">
            AG controls the scientific output. Hale controls final program approval.
          </p>
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1" style={{ color: MUTED }}>
            Conditions (one per line, optional)
          </p>
          <textarea
            rows={4}
            value={conditions}
            onChange={(e) => setConditions(e.target.value)}
            className="w-full text-sm p-2.5 rounded-sm border bg-white"
            style={{ borderColor: RULE, color: SLATE }}
            data-testid="reviewer-conditions"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-sm border p-3 text-[13px]"
             style={{ borderColor: 'rgba(176,49,45,0.4)', backgroundColor: 'rgba(176,49,45,0.05)', color: '#B0312D' }}
             data-testid="reviewer-error">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-sm border p-3 text-[13px]"
             style={{ borderColor: RULE, backgroundColor: 'rgba(22,98,75,0.06)', color: '#16624B' }}
             data-testid="reviewer-success">
          {success}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          onClick={saveDraft}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-[12px] font-mono uppercase tracking-wider border disabled:opacity-50"
          style={{ borderColor: SLATE, color: SLATE, backgroundColor: IVORY }}
          data-testid="reviewer-save-draft"
        >
          <FloppyDisk size={14} weight="bold" /> Save draft
        </button>
        <button
          onClick={commit}
          disabled={saving || handedOff}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm text-[12px] font-mono uppercase tracking-wider transition-transform hover:-translate-y-px disabled:opacity-50"
          style={{ backgroundColor: CORAL, color: SLATE }}
          data-testid="reviewer-commit"
        >
          <CheckCircle size={14} weight="fill" />
          {handedOff ? 'Already handed off to Hale' : 'Commit + route to Hale'}
        </button>
      </div>
    </div>
  );
}

export default function HaleReviewerPage() {
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [q, cfg] = await Promise.all([
        axios.get(`${API_URL}/api/hale-review/queue/ag`, { withCredentials: true }),
        axios.get(`${API_URL}/api/hale-review/config`, { withCredentials: true }),
      ]);
      setItems(q.data.items || []);
      setOptions(cfg.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load reviewer queue.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openApp = useMemo(() => items.find(a => a.id === openId), [items, openId]);

  // Compute summary KPIs from the live queue so they always match the
  // visible list (no separate stats endpoint to drift out of sync).
  const summary = useMemo(() => {
    const c = { intake: 0, docs: 0, sci: 0, ready: 0, routed: 0 };
    for (const a of items) {
      const ag = a.ag?.ag_status;
      const hale = a.hale?.hale_status;
      if (hale === 'ag_recommendation_received' || hale === 'hale_approved' ||
          hale === 'hale_rejected' || hale === 'hale_hold' || hale === 'badge_issued') {
        c.routed += 1;
      } else if (ag === 'scientific_output_complete' || ag === 'recommended' ||
                 ag === 'recommended_with_conditions' || ag === 'not_recommended') {
        c.ready += 1;
      } else if (ag === 'scientific_review') {
        c.sci += 1;
      } else if (ag === 'additional_information_required' || ag === 'document_review') {
        c.docs += 1;
      } else {
        c.intake += 1;
      }
    }
    return [
      { key: 'intake', label: 'Intake received',     value: c.intake },
      { key: 'docs',   label: 'Documents needed',    value: c.docs },
      { key: 'sci',    label: 'In scientific review', value: c.sci },
      { key: 'ready',  label: 'Output ready for Hale', value: c.ready },
      { key: 'routed', label: 'Routed to Hale',      value: c.routed },
    ];
  }, [items]);

  const handleSaved = (updated) => {
    setItems(arr => arr.map(a => a.id === updated.id ? updated : a));
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: IVORY, color: SLATE, fontFamily: 'Inter, system-ui, sans-serif' }}
      data-testid="ag-reviewer-page"
    >
      <Helmet><title>AG Reviewer · Hale Scientific Audit · ToxIntel</title></Helmet>

      <header className="border-b" style={{ borderColor: RULE }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3" aria-label="ToxIntel home">
              <span className="text-xl tracking-tight" style={{ color: SLATE, fontFamily: '"Cormorant Garamond", serif' }}>
                <TI color={CORAL} />
              </span>
            </Link>
            <span
              className="ml-3 text-[10px] font-mono uppercase tracking-[0.25em] px-2 py-1 rounded-sm"
              style={{ backgroundColor: SLATE, color: IVORY }}
            >
              · AG Reviewer · Scientific authority
            </span>
          </div>
          <nav className="flex items-center gap-5">
            <Link to="/admin/hale" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="reviewer-nav-back">
              ← Hale admin hub
            </Link>
            <Link to="/admin/hale/decisions" className="text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }} data-testid="reviewer-nav-decisions">
              Hale decisions →
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 lg:py-14">
        <div className="mb-10">
          <HaleWordmark size="md" />
          <p className="mt-5 text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
            Agyekum Group · ToxIntel scientific authority
          </p>
          <h1
            className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight"
            style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}
            data-testid="reviewer-title"
          >
            AG reviewer queue.
          </h1>
          <p className="mt-3 text-sm max-w-3xl" style={{ color: MUTED }}>
            Scientific audit, document review, and audit findings live here. The reviewer
            commits a recommendation (<span className="font-mono">Recommended</span> /
            <span className="font-mono"> with Conditions</span> /
            <span className="font-mono"> Additional Information Required</span> /
            <span className="font-mono"> Not Recommended</span>) — the final Hale program
            decision and badge issuance live in the Hale decisions console.
          </p>
        </div>

        {loading && <p className="text-sm font-mono uppercase tracking-wider" style={{ color: MUTED }} data-testid="reviewer-loading">Loading queue…</p>}
        {error && (
          <div className="rounded-sm border p-5"
               style={{ borderColor: 'rgba(176,49,45,0.4)', backgroundColor: 'rgba(176,49,45,0.05)', color: '#B0312D' }}
               data-testid="reviewer-error-banner">
            <Warning size={18} weight="fill" className="inline mr-2" /> {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Reviewer summary cards — computed from the live AG queue. */}
            <SummaryCards items={summary} testidPrefix="reviewer-summary" />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Queue */}
            <aside className="lg:col-span-2">
              <div className="rounded-sm border" style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.6)' }}>
                <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: RULE }}>
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: CORAL }}>
                    Queue · {items.length}
                  </p>
                </div>
                <ul data-testid="reviewer-queue-list" className="max-h-[640px] overflow-y-auto">
                  {items.length === 0 && (
                    <li className="p-5 text-sm" style={{ color: MUTED }}>Queue is empty — every submission has been actioned.</li>
                  )}
                  {items.map(a => (
                    <li
                      key={a.id}
                      className="p-4 border-b cursor-pointer hover:bg-white/60"
                      style={{ borderColor: RULE, backgroundColor: openId === a.id ? 'rgba(255,78,106,0.06)' : 'transparent' }}
                      onClick={() => setOpenId(a.id)}
                      data-testid={`reviewer-queue-item-${a.id}`}
                    >
                      <p className="text-sm font-semibold" style={{ color: SLATE }}>{a.brand_name || '(unnamed brand)'}</p>
                      <p className="text-[11px] font-mono uppercase tracking-wider mt-1" style={{ color: MUTED }}>
                        {a.email_masked || a.email || ''}
                      </p>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        <StatusPill value={a.ag?.ag_status} options={options?.ag_statuses} accent={CORAL} />
                        <StatusPill value={a.hale?.hale_status} options={options?.hale_statuses} accent={MUTED} />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Editor */}
            <section className="lg:col-span-3">
              {!openApp && (
                <div className="rounded-sm border p-10 text-center"
                     style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
                     data-testid="reviewer-no-selection">
                  <Flask size={32} weight="duotone" style={{ color: CORAL }} className="mx-auto mb-3" />
                  <p className="text-base font-semibold" style={{ color: SLATE }}>Pick a submission to begin the scientific audit.</p>
                  <p className="mt-2 text-sm" style={{ color: MUTED }}>
                    The queue shows AG-actionable submissions. Internal notes never leave
                    AG. Released findings go to Hale and (after a Hale decision) to the brand.
                  </p>
                </div>
              )}

              {openApp && (
                <div className="rounded-sm border p-7"
                     style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.7)' }}
                     data-testid="reviewer-detail">
                  <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
                    <div>
                      <p className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: CORAL }}>
                        Scientific audit · application {openApp.id.slice(-6).toUpperCase()}
                      </p>
                      <h2 className="text-2xl font-extrabold tracking-tight mt-1"
                          style={{ fontFamily: '"Cormorant Garamond", serif', color: SLATE }}>
                        {openApp.brand_name || '(unnamed brand)'}
                      </h2>
                      <p className="text-[12px] mt-1" style={{ color: MUTED }}>
                        {openApp.founder ? `${openApp.founder} · ` : ''}{openApp.email}
                      </p>
                    </div>
                    <div className="text-right">
                      <StatusPill value={openApp.ag?.ag_status} options={options?.ag_statuses} accent={CORAL} />
                      <p className="text-[10px] font-mono uppercase tracking-wider mt-2" style={{ color: MUTED }}>
                        Brand sees: {openApp.brand_status?.replaceAll('_', ' ')}
                      </p>
                    </div>
                  </div>

                  <SubmissionSnapshot app={openApp} />

                  <div className="my-5">
                    <AuditTimeline
                      stages={['Intake received', 'Document review', 'Scientific review', 'Recommendation complete', 'Routed to Hale']}
                      currentIndex={agStageIndex(openApp)}
                      testid="reviewer-audit-timeline"
                    />
                  </div>

                  <ReviewerEditor app={openApp} options={options} onSaved={handleSaved} />
                </div>
              )}
            </section>
          </div>
          </>
        )}
      </main>
    </div>
  );
}

/* Compact submission snapshot — visible above the editor for the picked
 * application. Reads only from the fields the API already surfaces; no
 * extra fetches. The Hale spec calls for: brand, submitted by, pathway,
 * services, missing documents, priority, date submitted. */
function SubmissionSnapshot({ app }) {
  const draft = app.draft || {};
  const brand = draft.brand || {};
  const products = draft.products || {};
  const founder = draft.founder || {};
  const missing = (app.ag?.document_checklist || [])
    .filter(r => r.status === 'missing')
    .map(r => r.label);
  const pathway = brand.pathway_requested || products.pathway_requested ||
                  (app.recommendation_summary && app.recommendation_summary.pathway) || '—';
  const services = Array.isArray(products.services_requested)
    ? products.services_requested.join(', ')
    : (products.services_requested || '—');
  const priority = app.priority || (app.recommendation_summary?.priority) || 'standard';
  const submittedAt = app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '—';

  const rows = [
    { k: 'Brand',           v: app.brand_name || brand.brand_name || '—' },
    { k: 'Submitted by',    v: app.founder || founder.full_name || app.email || '—' },
    { k: 'Pathway',         v: pathway === '—' ? '—' : String(pathway).replace(/_/g, ' ') },
    { k: 'Services',        v: services },
    { k: 'Missing docs',    v: missing.length ? `${missing.length} · ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''}` : 'None flagged' },
    { k: 'Priority',        v: String(priority).toLowerCase() },
    { k: 'Date submitted',  v: submittedAt },
  ];

  return (
    <div
      className="rounded-sm border p-4 mb-5"
      style={{ borderColor: RULE, backgroundColor: 'rgba(255,255,255,0.5)' }}
      data-testid="reviewer-submission-snapshot"
    >
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] mb-3" style={{ color: MUTED }}>
        Submission snapshot
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {rows.map(r => (
          <div key={r.k} className="flex items-baseline justify-between gap-3">
            <dt className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: MUTED }}>
              {r.k}
            </dt>
            <dd className="text-[13px] font-semibold text-right truncate" style={{ color: SLATE, maxWidth: '60%' }}>
              {r.v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
